/**
 * The WhatsApp NUMBER REGISTRY of the Central (D636, doc 25 phase 1, owner Q-365: Meta's Cloud API direct, no BSP).
 *
 * One registered business number = one Meta phone number (its `phone_number_id`), the number a customer sees, the WhatsApp Business
 * Account it belongs to, and WHERE it serves: a country and, optionally, one operating market (the conversation it opens resolves
 * at that scope). The number never carries a secret: the access token, the app secret and the webhook verify token live in the
 * credential vault under slots derived from the number id ({@link numberCredentialSlot}); this record only says whether each is set.
 * The approved templates the Central may send outside the 24 h window are listed WITH their text preview, so the desk shows the
 * customer's real text rather than a template code.
 *
 * Pure and neutral: no country, language or currency is named; the country and the market are data the caller validates against its
 * registries (`countryCode` is ISO 3166-1 alpha-2, `marketId` is `cc:slug` of the same country, like every market id).
 */
import { err, ok } from '@vello/core';
import { QUALITY_RATINGS } from './delivery.js';
import { cpLength, hasControl, hasLoneSurrogate } from './plain-text.js';
export const NUMBER_STATUSES = ['PENDING', 'ACTIVE', 'PAUSED', 'RETIRED'];
/** Meta's template categories the Central knows how to treat (marketing needs an explicit contact opt-in). */
export const TEMPLATE_CATEGORIES = ['utility', 'authentication', 'marketing'];
export const NUMBER_REFUSAL_CODES = [
    'WA_NUMBER_NOT_FOUND',
    'WA_NUMBER_PHONE_ID_INVALID',
    'WA_NUMBER_DISPLAY_INVALID',
    'WA_NUMBER_WABA_INVALID',
    'WA_NUMBER_COUNTRY_INVALID',
    'WA_NUMBER_COUNTRY_NOT_REGISTERED',
    'WA_NUMBER_MARKET_INVALID',
    'WA_NUMBER_LABEL_INVALID',
    'WA_NUMBER_API_VERSION_INVALID',
    'WA_NUMBER_QUALITY_INVALID',
    'WA_NUMBER_TEMPLATE_INVALID',
    'WA_NUMBER_KEYWORD_INVALID',
    'WA_NUMBER_DUPLICATE',
    'WA_NUMBER_VERSION_CONFLICT',
    'WA_NUMBER_TRANSITION_ILLEGAL',
    'WA_NUMBER_CREDENTIALS_INCOMPLETE',
    'WA_NUMBER_RETIRED',
    'WA_NUMBER_NOTHING_TO_CHANGE',
    'WA_NUMBER_CREDENTIAL_INVALID',
];
const refuse = (code, message) => err({ code, message });
/** Meta's default opt-out word, an editable starting point (the registry holds the list a country actually wants). */
export const DEFAULT_OPT_OUT_KEYWORDS = ['stop'];
export const DEFAULT_GRAPH_API_VERSION = 'v21.0';
const E164 = /^\+[1-9]\d{6,14}$/;
const META_ID = /^\d{5,24}$/;
const COUNTRY = /^[A-Z]{2}$/;
const MARKET = /^([a-z]{2}):[a-z0-9]+(-[a-z0-9]+)*$/;
const API_VERSION = /^v\d{1,2}\.\d{1,2}$/;
const TEMPLATE_NAME = /^[a-z0-9_]{1,200}$/;
const TEMPLATE_LANGUAGE = /^[a-z]{2,3}(_[A-Z]{2})?$/;
const KEYWORD = /^[\p{L}\p{N}]{1,20}$/u;
export const MAX_TEMPLATES = 50;
export const MAX_KEYWORDS = 10;
/** The vault slot ids of one number's three secrets (write-only in every route; the AAD of each is its slot id). */
export const NUMBER_CREDENTIAL_KINDS = ['access_token', 'app_secret', 'verify_token'];
export function numberCredentialSlot(numberId, kind) {
    return `whatsapp.number.${numberId}.${kind}`;
}
/** The highest `{{n}}` placeholder of a template preview (0 when none). */
export function placeholderCount(preview) {
    let max = 0;
    for (const m of preview.matchAll(/\{\{(\d{1,2})\}\}/g))
        max = Math.max(max, Number(m[1]));
    return max;
}
/** Normalise an opt-out keyword the same way an inbound message is compared (trim, lower-case, NFC). */
export function normalizeKeyword(raw) {
    return raw.trim().normalize('NFC').toLowerCase();
}
export function validateTemplate(raw) {
    const t = typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? raw : undefined;
    const bad = (m) => refuse('WA_NUMBER_TEMPLATE_INVALID', m);
    if (t === undefined)
        return bad('a template is an object');
    const { name, language, category, preview, paramCount } = t;
    if (typeof name !== 'string' || !TEMPLATE_NAME.test(name))
        return bad('name: lower-case letters, digits and underscore');
    if (typeof language !== 'string' || !TEMPLATE_LANGUAGE.test(language))
        return bad('language: a Meta language code such as fr_FR or es');
    if (typeof category !== 'string' || !TEMPLATE_CATEGORIES.includes(category))
        return bad(`category: one of ${TEMPLATE_CATEGORIES.join(', ')}`);
    if (typeof preview !== 'string' || preview.trim() === '' || cpLength(preview) > 1000 || hasControl(preview, true) || hasLoneSurrogate(preview))
        return bad('preview: 1..1000 characters of text');
    const wanted = placeholderCount(preview);
    if (typeof paramCount !== 'number' || !Number.isInteger(paramCount) || paramCount < 0 || paramCount > 10)
        return bad('paramCount: a whole number from 0 to 10');
    if (paramCount !== wanted)
        return bad(`paramCount must equal the highest placeholder of the preview (${wanted})`);
    return ok({ name, language, category: category, preview: preview.trim(), paramCount });
}
function validateTemplates(raw) {
    if (!Array.isArray(raw))
        return refuse('WA_NUMBER_TEMPLATE_INVALID', 'templates is a list');
    if (raw.length > MAX_TEMPLATES)
        return refuse('WA_NUMBER_TEMPLATE_INVALID', `at most ${MAX_TEMPLATES} templates`);
    const out = [];
    for (const r of raw) {
        const t = validateTemplate(r);
        if (!t.ok)
            return t;
        if (out.some((x) => x.name === t.value.name && x.language === t.value.language))
            return refuse('WA_NUMBER_TEMPLATE_INVALID', `duplicate template ${t.value.name} (${t.value.language})`);
        out.push(t.value);
    }
    return ok(out);
}
function validateKeywords(raw) {
    if (!Array.isArray(raw) || raw.length > MAX_KEYWORDS)
        return refuse('WA_NUMBER_KEYWORD_INVALID', `a list of at most ${MAX_KEYWORDS} words`);
    const out = [];
    for (const r of raw) {
        if (typeof r !== 'string')
            return refuse('WA_NUMBER_KEYWORD_INVALID', 'a word is text');
        const k = normalizeKeyword(r);
        if (!KEYWORD.test(k))
            return refuse('WA_NUMBER_KEYWORD_INVALID', 'a word is 1..20 letters or digits');
        if (!out.includes(k))
            out.push(k);
    }
    return ok(out);
}
function validateLabel(raw) {
    if (raw === undefined || raw === null || raw === '')
        return ok(undefined);
    if (typeof raw !== 'string')
        return refuse('WA_NUMBER_LABEL_INVALID', 'label is text');
    const label = raw.trim();
    if (label === '' || cpLength(label) > 80 || hasControl(label) || hasLoneSurrogate(label))
        return refuse('WA_NUMBER_LABEL_INVALID', 'label: 1..80 characters on one line');
    return ok(label);
}
/** A market id must be `cc:slug` of the number's own country. */
export function marketBelongsToCountry(marketId, countryCode) {
    const m = MARKET.exec(marketId);
    return m !== null && m[1].toUpperCase() === countryCode;
}
/** Register a number: always PENDING (it cannot serve before its three secrets are set and an operator activates it). */
export function registerBusinessNumber(input, now) {
    if (typeof input.phoneNumberId !== 'string' || !META_ID.test(input.phoneNumberId))
        return refuse('WA_NUMBER_PHONE_ID_INVALID', 'phoneNumberId: the digits of Meta\'s phone number id');
    if (typeof input.displayNumber !== 'string' || !E164.test(input.displayNumber))
        return refuse('WA_NUMBER_DISPLAY_INVALID', 'displayNumber: an E.164 number with the country calling code');
    if (typeof input.wabaId !== 'string' || !META_ID.test(input.wabaId))
        return refuse('WA_NUMBER_WABA_INVALID', 'wabaId: the digits of the WhatsApp Business Account id');
    if (typeof input.countryCode !== 'string' || !COUNTRY.test(input.countryCode))
        return refuse('WA_NUMBER_COUNTRY_INVALID', 'countryCode: two capital letters (ISO 3166-1 alpha-2)');
    const countryCode = input.countryCode;
    let marketId;
    if (input.marketId !== undefined && input.marketId !== null && input.marketId !== '') {
        if (typeof input.marketId !== 'string' || !marketBelongsToCountry(input.marketId, countryCode))
            return refuse('WA_NUMBER_MARKET_INVALID', 'marketId: a market of the number\'s own country (cc:slug)');
        marketId = input.marketId;
    }
    const label = validateLabel(input.label);
    if (!label.ok)
        return label;
    const apiVersion = input.apiVersion === undefined ? DEFAULT_GRAPH_API_VERSION : input.apiVersion;
    if (typeof apiVersion !== 'string' || !API_VERSION.test(apiVersion))
        return refuse('WA_NUMBER_API_VERSION_INVALID', 'apiVersion: a Graph API version such as v21.0');
    const templates = validateTemplates(input.templates ?? []);
    if (!templates.ok)
        return templates;
    const keywords = validateKeywords(input.optOutKeywords ?? DEFAULT_OPT_OUT_KEYWORDS);
    if (!keywords.ok)
        return keywords;
    return ok({
        numberId: input.numberId,
        phoneNumberId: input.phoneNumberId,
        displayNumber: input.displayNumber,
        wabaId: input.wabaId,
        countryCode,
        ...(marketId !== undefined ? { marketId } : {}),
        ...(label.value !== undefined ? { label: label.value } : {}),
        status: 'PENDING',
        apiVersion,
        qualityRating: 'GREEN',
        templates: templates.value,
        optOutKeywords: keywords.value,
        version: 1,
        createdAt: now,
        createdBy: input.createdBy,
        updatedAt: now,
        updatedBy: input.createdBy,
    });
}
/** Edit the mutable fields. The Meta phone number id and the country never change (a conversation snapshotted them); a RETIRED number is frozen. */
export function editBusinessNumber(n, edit, by, now) {
    if (n.status === 'RETIRED')
        return refuse('WA_NUMBER_RETIRED', 'a retired number is frozen');
    let next = n;
    let changed = false;
    if (edit.displayNumber !== undefined) {
        if (typeof edit.displayNumber !== 'string' || !E164.test(edit.displayNumber))
            return refuse('WA_NUMBER_DISPLAY_INVALID', 'displayNumber: an E.164 number with the country calling code');
        if (edit.displayNumber !== n.displayNumber) {
            next = { ...next, displayNumber: edit.displayNumber };
            changed = true;
        }
    }
    if (edit.wabaId !== undefined) {
        if (typeof edit.wabaId !== 'string' || !META_ID.test(edit.wabaId))
            return refuse('WA_NUMBER_WABA_INVALID', 'wabaId: the digits of the WhatsApp Business Account id');
        if (edit.wabaId !== n.wabaId) {
            next = { ...next, wabaId: edit.wabaId };
            changed = true;
        }
    }
    if (edit.marketId !== undefined) {
        if (edit.marketId === null || edit.marketId === '') {
            if (n.marketId !== undefined) {
                const { marketId: _m, ...rest } = next;
                next = rest;
                changed = true;
            }
        }
        else {
            if (typeof edit.marketId !== 'string' || !marketBelongsToCountry(edit.marketId, n.countryCode))
                return refuse('WA_NUMBER_MARKET_INVALID', 'marketId: a market of the number\'s own country (cc:slug)');
            if (edit.marketId !== n.marketId) {
                next = { ...next, marketId: edit.marketId };
                changed = true;
            }
        }
    }
    if (edit.label !== undefined) {
        const label = validateLabel(edit.label);
        if (!label.ok)
            return label;
        if (label.value !== n.label) {
            if (label.value === undefined) {
                const { label: _l, ...rest } = next;
                next = rest;
            }
            else
                next = { ...next, label: label.value };
            changed = true;
        }
    }
    if (edit.apiVersion !== undefined) {
        if (typeof edit.apiVersion !== 'string' || !API_VERSION.test(edit.apiVersion))
            return refuse('WA_NUMBER_API_VERSION_INVALID', 'apiVersion: a Graph API version such as v21.0');
        if (edit.apiVersion !== n.apiVersion) {
            next = { ...next, apiVersion: edit.apiVersion };
            changed = true;
        }
    }
    if (edit.qualityRating !== undefined) {
        if (typeof edit.qualityRating !== 'string' || !QUALITY_RATINGS.includes(edit.qualityRating))
            return refuse('WA_NUMBER_QUALITY_INVALID', `qualityRating: one of ${QUALITY_RATINGS.join(', ')}`);
        if (edit.qualityRating !== n.qualityRating) {
            next = { ...next, qualityRating: edit.qualityRating };
            changed = true;
        }
    }
    if (edit.templates !== undefined) {
        const templates = validateTemplates(edit.templates);
        if (!templates.ok)
            return templates;
        if (JSON.stringify(templates.value) !== JSON.stringify(n.templates)) {
            next = { ...next, templates: templates.value };
            changed = true;
        }
    }
    if (edit.optOutKeywords !== undefined) {
        const keywords = validateKeywords(edit.optOutKeywords);
        if (!keywords.ok)
            return keywords;
        if (JSON.stringify(keywords.value) !== JSON.stringify(n.optOutKeywords)) {
            next = { ...next, optOutKeywords: keywords.value };
            changed = true;
        }
    }
    if (!changed)
        return refuse('WA_NUMBER_NOTHING_TO_CHANGE', 'nothing to change');
    return ok({ ...next, version: n.version + 1, updatedAt: now, updatedBy: by });
}
const TRANSITIONS = {
    PENDING: ['ACTIVE', 'RETIRED'],
    ACTIVE: ['PAUSED', 'RETIRED'],
    PAUSED: ['ACTIVE', 'RETIRED'],
    RETIRED: [],
};
export function canTransitionNumber(from, to) {
    return TRANSITIONS[from].includes(to);
}
/** Move a number along PENDING -> ACTIVE <-> PAUSED -> RETIRED. Activating needs all three secrets set (`credentialsComplete`). */
export function transitionBusinessNumber(n, to, credentialsComplete, by, now) {
    if (!canTransitionNumber(n.status, to))
        return refuse(n.status === 'RETIRED' ? 'WA_NUMBER_RETIRED' : 'WA_NUMBER_TRANSITION_ILLEGAL', `${n.status} -> ${to}`);
    if (to === 'ACTIVE' && !credentialsComplete)
        return refuse('WA_NUMBER_CREDENTIALS_INCOMPLETE', 'set the access token, the app secret and the verify token first');
    return ok({ ...n, status: to, version: n.version + 1, updatedAt: now, updatedBy: by });
}
/** The scope a conversation opened through this number resolves under (platform -> country -> market). */
export function numberScope(n) {
    return { platform: 'vello', countryCode: n.countryCode, ...(n.marketId !== undefined ? { marketId: n.marketId } : {}) };
}
/** A credential value (token or secret) before it is sealed: one token, no whitespace, a sane length. */
export function validateCredentialValue(raw) {
    if (typeof raw !== 'string' || raw.length < 8 || raw.length > 1024 || /\s/.test(raw))
        return refuse('WA_NUMBER_CREDENTIAL_INVALID', 'a credential is one value of 8..1024 characters with no spaces');
    return ok(raw);
}
/** The template of the number for a name and language, when approved. */
export function findTemplate(n, name, language) {
    return n.templates.find((t) => t.name === name && t.language === language);
}
//# sourceMappingURL=business-number.js.map